import { Divider, Select, Space, theme } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  HighlightOutlined,
  CodeOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UndoOutlined,
  RedoOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import LinkPopover from './LinkPopover';
import TableControls from './TableControls';
import InsertMenu from './InsertMenu';
import ImageInsertButtons from './ImageInsertButtons';
import ToolbarButton from './ToolbarButton';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * 完整工具列：區塊樣式、行內格式、清單、對齊、連結、圖片、表格、插入選單。
 * 圖片按鈕的動作來自 ImagePickerContext（由 Tiptap.tsx 提供）。
 * 手機：單列可橫向捲動、黏在捲動容器頂端（#app-content），按鈕 32px、無 Tooltip。
 */
export default function Toolbar({ editor }: { editor: Editor }) {
  const { token } = theme.useToken();
  const mobile = useIsMobile();
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      highlight: e.isActive('highlight'),
      code: e.isActive('code'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      link: e.isActive('link'),
      table: e.isActive('table'),
      block: e.isActive('heading', { level: 1 })
        ? 'h1'
        : e.isActive('heading', { level: 2 })
          ? 'h2'
          : e.isActive('heading', { level: 3 })
            ? 'h3'
            : 'p',
    }),
  });

  const c = () => editor.chain().focus();
  const tb = (active: boolean, icon: React.ReactNode, on: () => void, tip: string) => (
    <ToolbarButton active={active} icon={icon} onClick={on} tip={tip} />
  );

  const setBlock = (v: string) => {
    if (v === 'p') c().setParagraph().run();
    else c().toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run();
  };

  const items = (
    <>
      {tb(false, <UndoOutlined />, () => c().undo().run(), '復原')}
      {tb(false, <RedoOutlined />, () => c().redo().run(), '重做')}
      <Divider type="vertical" />
      <Select
        size={mobile ? 'middle' : 'small'}
        value={s.block}
        style={{ width: 92 }}
        onChange={setBlock}
        options={[
          { value: 'p', label: '內文' },
          { value: 'h1', label: '標題 1' },
          { value: 'h2', label: '標題 2' },
          { value: 'h3', label: '標題 3' },
        ]}
      />
      <Divider type="vertical" />
      {tb(s.bold, <BoldOutlined />, () => c().toggleBold().run(), '粗體')}
      {tb(s.italic, <ItalicOutlined />, () => c().toggleItalic().run(), '斜體')}
      {tb(s.underline, <UnderlineOutlined />, () => c().toggleUnderline().run(), '底線')}
      {tb(s.strike, <StrikethroughOutlined />, () => c().toggleStrike().run(), '刪除線')}
      {tb(s.highlight, <HighlightOutlined />, () => c().toggleHighlight().run(), '螢光標記')}
      {tb(s.code, <CodeOutlined />, () => c().toggleCode().run(), '行內碼')}
      <Divider type="vertical" />
      {tb(s.bullet, <UnorderedListOutlined />, () => c().toggleBulletList().run(), '項目清單')}
      {tb(s.ordered, <OrderedListOutlined />, () => c().toggleOrderedList().run(), '編號清單')}
      {tb(s.quote, '“”', () => c().toggleBlockquote().run(), '引言')}
      <Divider type="vertical" />
      {tb(false, <AlignLeftOutlined />, () => c().setTextAlign('left').run(), '靠左')}
      {tb(false, <AlignCenterOutlined />, () => c().setTextAlign('center').run(), '置中')}
      {tb(false, <AlignRightOutlined />, () => c().setTextAlign('right').run(), '靠右')}
      <Divider type="vertical" />
      <LinkPopover editor={editor} active={s.link} />
      <TableControls editor={editor} inTable={s.table} />
      {tb(false, <MinusOutlined />, () => c().setHorizontalRule().run(), '分隔線')}
      <Divider type="vertical" />
      <InsertMenu editor={editor} />
      <Divider type="vertical" />
      <ImageInsertButtons />
    </>
  );

  if (mobile)
    return (
      <div className="sticky -top-4 z-10 flex flex-nowrap items-center gap-0.5 overflow-x-auto rounded-t-lg border-b border-gray-200 bg-white px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
        {items}
      </div>
    );
  return (
    <Space wrap size={2} className="border-b border-gray-200 p-2" style={{ background: token.colorFillQuaternary }}>
      {items}
    </Space>
  );
}
